import { Radio, RadioGroup, Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import familySpaceImg from 'img/familySpace/img.png';
import { ReactComponent as Icon20InfoCircle } from 'img/icons/vkui/info_circle_outline_20.svg';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import {
    familySpaceUpsaleEnable,
    familyUpsaleEnable,
    multiTariffSpaceToEachFamilyMember,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import {
    type MultiTariffUpsaleModelType,
    MultiTariffUpsaleRadioType,
} from 'reactApp/sections/QuotaLanding/multiTariffUpsaleDialog/models/MultiTariffUpsaleDialog.model.types';
import { FamilySpaceUpsaleTooltip } from 'reactApp/sections/QuotaLanding/multiTariffUpsaleDialog/tooltip/FamilySpaceUpsaleTooltip';
import { useFamilySpaceTooltip } from 'reactApp/sections/QuotaLanding/multiTariffUpsaleDialog/tooltip/useFamilySpaceTooltip';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import type { Product } from 'reactApp/types/Billing';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './MultiTariffUpsaleDialog.css';

export type MultiTariffUpsaleDialogProps = {
    upsaleModel: MultiTariffUpsaleModelType;
    onClose?: () => void;
    resolve: (product: Product) => void;
};

export const MultiTariffUpsaleDialog = memo<MultiTariffUpsaleDialogProps>(({ upsaleModel, onClose, resolve }) => {
    const title = upsaleModel.getTitle();
    const text = upsaleModel.getTitleDescription();
    const secondProduct = upsaleModel.getSecondProduct();
    const upsaleProduct = upsaleModel.getUpsaleProduct();
    const firstAlternativeProduct = upsaleModel?.getFirstAlternativeProduct?.();
    const secondaryCardLabelText = upsaleModel.getSecondaryCardLabelText();

    const [chosenTariff, setChosenTariff] = useState<MultiTariffUpsaleRadioType>(MultiTariffUpsaleRadioType.priority);
    const isPriority: boolean = chosenTariff === MultiTariffUpsaleRadioType.priority;
    const isSecondary: boolean = chosenTariff === MultiTariffUpsaleRadioType.second;
    const isFirstAlternative: boolean = chosenTariff === MultiTariffUpsaleRadioType.firstAlternative;
    const isFamilySpaceUpsale = familySpaceUpsaleEnable && familyUpsaleEnable && multiTariffSpaceToEachFamilyMember;

    const tariffSpace = upsaleProduct?.space.space;
    const tariffUnits = upsaleProduct?.space.units;

    const { tooltipRef, setShowTooltip, showTooltip } = useFamilySpaceTooltip(isFamilySpaceUpsale);

    const chageShowTooltip = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setShowTooltip(true);
    };

    const sendAnalytics = (label: string, data: MultiTariffUpsaleRadioType) => {
        sendQuotaGa({
            label,
            ...upsaleModel.getDwh(data),
        });
    };

    useEffect(() => {
        sendAnalytics('show', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL_SHOW);
    }, []);

    const switchTariff = useCallback(
        (tariff: MultiTariffUpsaleRadioType) => {
            setChosenTariff(tariff);
            sendAnalytics('switch', tariff);
            emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL_SWITCH);
        },
        [setChosenTariff]
    );

    const renderHeader = useCallback(
        () => (
            <>
                <Title>{title}</Title>
                <Spacing size={12} />
                <Text className={styles.headerText}>{text}</Text>
            </>
        ),
        [title, text]
    );

    const upsaleDesc = isFamilySpaceUpsale ? (
        <div className={styles.spaceDescription}>
            <div className={styles.familySizeWrap}>
                <Text className={styles.familySpaceSize}>
                    <span className={styles.familySpaceSizePlus}>+</span>
                    <span className={styles.familySpaceSizeNumber}>{tariffSpace}</span>
                    {tariffUnits}
                </Text>
                <img src={familySpaceImg} alt="Лица" className={styles.familySpaceImg} />
            </div>
            <div className={styles.familySpaceButton}>
                <Title className={styles.familySpaceButtonTitle}>{upsaleModel.getPriorityCardTitle()}</Title>
                <Text className={styles.familySpaceButtonDescription}>{upsaleModel.getPriorityCardDescription()}</Text>
            </div>
        </div>
    ) : (
        <>
            <Text className={styles.radioDescription}>{upsaleModel.getPriorityCardTopTitle()}</Text>
            <Title className={styles.radioTitle}>{upsaleModel.getPriorityCardTitle()}</Title>
            <Text className={styles.radioDescription}>{upsaleModel.getPriorityCardDescription()}</Text>
        </>
    );

    const defaultDesc = isFamilySpaceUpsale ? (
        <div className={styles.spaceDescription}>
            <div className={styles.familySizeWrap}>
                <Text className={styles.familySpaceSize}>
                    <span className={styles.familySpaceSizePlus}>+</span>
                    <span className={styles.familySpaceSizeNumber}>{tariffSpace}</span>
                    {tariffUnits}
                </Text>
            </div>
            <div className={styles.familySpaceButton}>
                <Title className={styles.familySpaceButtonTitle}>{upsaleModel.getSecondaryCardTitle()}</Title>
                <Text className={styles.familySpaceButtonDescription}>{upsaleModel.getSecondaryCardDescription()}</Text>
            </div>
        </div>
    ) : (
        <div className={styles.radioWrapper}>
            <Text className={styles.radioDescription}>{upsaleModel.getSecondaryCardTopTitle()}</Text>
            <Title className={styles.radioTitle}>{upsaleModel.getSecondaryCardTitle()}</Title>
            <Text className={styles.radioDescription}>{upsaleModel.getSecondaryCardDescription()}</Text>
        </div>
    );

    const renderContent = useCallback(() => {
        return (
            <RadioGroup className={styles.radioGroup}>
                <Radio
                    className={classNames(styles.radio, {
                        [styles['radio_active']]: isPriority,
                        [styles.radioFamilySpace]: isFamilySpaceUpsale,
                    })}
                    name="activePeriod"
                    value="year"
                    description={upsaleDesc}
                    checked={isPriority}
                    onClick={() => switchTariff(MultiTariffUpsaleRadioType.priority)}
                >
                    {isFamilySpaceUpsale && upsaleProduct?.isFamily ? (
                        <div className={styles.labelWrap}>
                            <div
                                className={classNames(styles.spaceLabel, {
                                    [styles['spaceLabel_active']]: isPriority,
                                })}
                            >
                                {upsaleModel.getPriorityCardLabelText()}
                            </div>
                            <div
                                className={classNames(styles.spaceLabel, {
                                    [styles['spaceLabel_family']]: true,
                                })}
                                ref={tooltipRef}
                                onClick={chageShowTooltip}
                            >
                                {showTooltip && <FamilySpaceUpsaleTooltip ref={tooltipRef} onClose={setShowTooltip} />}
                                {tariffSpace} {tariffUnits} каждому
                                <Icon20InfoCircle className={styles.familyLabelIcon} fill={'#2B6317'} />
                            </div>
                        </div>
                    ) : (
                        <div
                            className={classNames(styles.label, {
                                [styles.labelActive]: isPriority,
                            })}
                        >
                            {upsaleModel.getPriorityCardLabelText()}
                        </div>
                    )}
                </Radio>

                {!!firstAlternativeProduct && (
                    <Radio
                        className={classNames(styles.radio, {
                            [styles['radio_active']]: isFirstAlternative,
                        })}
                        name="activePeriod"
                        value="firstAlternative"
                        description={
                            <>
                                <Text className={styles.radioDescription}>{upsaleModel?.getFirstAlternativeCardTopTitle?.()}</Text>
                                <Title className={styles.radioTitle}>{upsaleModel?.getFirstAlternativeCardTitle?.()}</Title>
                                <Text className={styles.radioDescription}>{upsaleModel?.getFirstAlternativeCardDescription?.()}</Text>
                            </>
                        }
                        checked={isFirstAlternative}
                        onClick={() => switchTariff(MultiTariffUpsaleRadioType.firstAlternative)}
                    >
                        <div
                            className={classNames(styles.label, {
                                [styles.labelActive]: isFirstAlternative,
                            })}
                        >
                            {upsaleModel?.getFirstAlternativeCardLabelText?.()}
                        </div>
                    </Radio>
                )}

                <Radio
                    className={classNames(styles.radio, {
                        [styles['radio_active']]: isSecondary,
                        [styles.radioFamilySpace]: isFamilySpaceUpsale,
                    })}
                    name="activePeriod"
                    value="month"
                    description={defaultDesc}
                    checked={isSecondary}
                    onClick={() => switchTariff(MultiTariffUpsaleRadioType.second)}
                >
                    {isFamilySpaceUpsale ? (
                        <div className={styles.labelWrap}>
                            <div
                                className={classNames(styles.spaceLabel, {
                                    [styles['spaceLabel_active']]: isSecondary,
                                })}
                            >
                                {secondaryCardLabelText}
                            </div>
                        </div>
                    ) : (
                        <div
                            className={classNames(styles.label, {
                                [styles.labelActive]: isSecondary,
                                [styles.labelNone]: !secondaryCardLabelText,
                            })}
                        >
                            {secondaryCardLabelText}
                        </div>
                    )}
                </Radio>
            </RadioGroup>
        );
    }, [chosenTariff, showTooltip]);

    const handleClose = useCallback(() => {
        onClose?.();
        sendAnalytics('close', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL_CLOSE);
    }, [onClose, chosenTariff]);

    const handleSuccess = useCallback(() => {
        sendAnalytics('continue', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_MULTI_MODAL__CONTINUE);
        onClose?.();

        let productToResolve: Product | undefined;
        switch (chosenTariff) {
            case MultiTariffUpsaleRadioType.firstAlternative:
                productToResolve = firstAlternativeProduct;
                break;
            case MultiTariffUpsaleRadioType.second:
                productToResolve = secondProduct;
                break;
            case MultiTariffUpsaleRadioType.priority:
            default:
                productToResolve = upsaleProduct;
                break;
        }

        if (productToResolve) {
            resolve(productToResolve);
        }
    }, [chosenTariff, secondProduct, upsaleProduct, firstAlternativeProduct]);

    const containsDarkTheme = isDarkThemeModeEnabled();

    return (
        <BaseConfirmDialog
            onClose={handleClose}
            closeOnDimmerClick
            renderHeader={renderHeader}
            renderContent={renderContent}
            buttons={[EButtonType.accept]}
            buttonTheme="vk"
            successButtonText="Продолжить"
            onSuccess={handleSuccess}
            isDarkTheme={containsDarkTheme}
            className={classNames(styles.root, { [styles.alternativeRoot]: !!firstAlternativeProduct })}
        />
    );
});

MultiTariffUpsaleDialog.displayName = 'MultiTariffUpsaleDialog';
